import type { PrivacySettings } from '../types/person';

/**
 * Firestore collection paths.
 *
 * ID conventions:
 * - Most collections use auto-generated IDs.
 * - `eventParticipants` use composite IDs: `{eventId}_{personId}` — enables
 *   deterministic lookups in security rules via get()/exists().
 * - `persons` use a server-generated stable id (nanoid). `authAccounts`
 *   uses the Firebase Auth uid as the doc ID and stores a pointer to the
 *   matching `persons` row.
 */
export const Collections = {
  EVENTS: 'events',
  SESSIONS: 'sessions',
  SESSION_NOTES: 'sessionNotes',
  SPONSORS: 'sponsors',
  NOTIFICATIONS: 'notifications',
  NOTIFICATION_RECEIPTS: 'notificationReceipts',
  SURVEYS: 'surveys',
  SURVEY_RESPONSES: 'surveyResponses',
  EMAIL_DELIVERIES: 'emailDeliveries',
  /** Watched by the Trigger Email extension (firebase/firestore-send-email). */
  MAIL: 'mail',
  /** Per-session operational logistics. Document ID = session ID. */
  SESSION_INTERNALS: 'sessionInternals',
  /**
   * Per-event audience primitive — the unified "who can see this" type.
   * Replaces the `eventGroups` collection. Holds custom groups, role
   * audiences (system-bootstrapped), personal audiences (one per user
   * per event), and anonymous compositions. See ADR-120.
   */
  AUDIENCES: 'audiences',
  /**
   * Per-event standalone map pins (the hotel, ski rental shop, dinner
   * restaurant). Linked pins (sessions, travel items, the venue) are
   * NOT in this collection -- they're rendered from their source
   * entities. See ADR-096 amendment.
   */
  EVENT_LOCATIONS: 'eventLocations',
  /** Per-event travel records with audience-scoped reminders. See ADR-094. */
  TRAVEL_ITEMS: 'travelItems',
  /** Admin-created invitation records with per-attendee codes. */
  INVITATIONS: 'invitations',
  /** CF-only weather forecast cache with Firestore TTL. See ADR-096. */
  WEATHER_CACHE: 'weatherCache',
  /**
   * ADR-136 — Stable Person Identity Model. Durable identity record;
   * replaces the legacy `users` + `contacts` dual-record pattern. PersonId
   * is a server-generated stable id.
   */
  PERSONS: 'persons',
  /** ADR-136 — Private contact tier for persons (replaces userProfiles). */
  PERSON_PROFILES: 'personProfiles',
  /** ADR-136 — Internal-only tier for persons (replaces internalProfiles). */
  PERSON_INTERNALS: 'personInternals',
  /**
   * ADR-136 — Thin auth-account pointer keyed by Firebase Auth uid.
   * Holds `personId` for cheap uid → personId resolution at the
   * security-rules layer.
   */
  AUTH_ACCOUNTS: 'authAccounts',
  /**
   * ADR-136 — Per-event participation, keyed by personId (composite ID
   * `{eventId}_{personId}`). Replaces the legacy uid-keyed
   * `eventMemberships`, which couldn't represent pre-auth participation.
   */
  EVENT_PARTICIPANTS: 'eventParticipants',
} as const;

export type CollectionName = (typeof Collections)[keyof typeof Collections];

/** Directory visibility defaults to off (opt-in model per PRD F-009). */
export const DEFAULT_PRIVACY_SETTINGS: PrivacySettings = {
  showInDirectory: false,
};
